import {
  BanknotesIcon,
  CalendarDaysIcon,
  ClockIcon,
  CreditCardIcon,
  EnvelopeIcon,
  PhoneIcon,
  TicketIcon,
  UserIcon,
  UsersIcon,
} from "@heroicons/react/24/solid";
import type { ComponentType, ReactNode } from "react";
import {
  formatBookingAmount,
  formatDateString,
  formatMad,
} from "../../../helpers/general";
import {
  bookingCommissionMad,
  bookingCustomer,
  bookingDueToVenez,
} from "../../booking-detail";

export const bookingTitle = (booking: any) =>
  booking.special?.title ||
  booking.event?.name ||
  booking.activity?.title ||
  booking.activityOption?.title ||
  "Activité Venez";

const paymentMethodLabel = (method?: string) => {
  const labels: Record<string, string> = {
    in_place: "Sur place (espèces)",
    card: "Carte bancaire",
    stripe: "Stripe",
    wafacash: "Wafacash",
  };
  return (method && labels[method]) || method || "—";
};

export function BookingStatusBadge({ value }: { value?: string }) {
  const status = value?.toLowerCase();
  const tone =
    status === "confirmed"
      ? "success"
      : status === "pending"
        ? "warning"
        : status?.startsWith("cancel")
          ? "danger"
          : "secondary";
  return <span className={`badge bg-${tone}`}>{value ?? "—"}</span>;
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="mb-4">
      <p className="text-xs fw-bold text-uppercase text-secondary mb-2">
        {title}
      </p>
      <div className="bg-light rounded-3 px-3 py-1">{children}</div>
    </div>
  );
}

function Row({
  icon: Icon,
  label,
  value,
  accent,
}: {
  icon?: ComponentType<{ width?: number; height?: number }>;
  label: string;
  value?: string;
  accent?: boolean;
}) {
  return (
    <div className="d-flex align-items-center justify-content-between gap-3 py-2 border-bottom border-white">
      <span className="d-flex align-items-center gap-2 text-sm text-secondary">
        {Icon && <Icon width={16} height={16} />}
        {label}
      </span>
      <span
        className={`text-sm fw-semibold text-end text-break ${accent ? "text-success" : ""}`}
      >
        {value || "—"}
      </span>
    </div>
  );
}

export default function PartnerBookingDetail({ booking }: { booking: any }) {
  const customer = bookingCustomer(booking);
  const commission = bookingCommissionMad(booking);
  const cashCollected = booking.paymentMethod === "in_place";
  const variants = booking.variantsBookings?.filter(
    (item: any) => item.variant?.description,
  );
  const confirmed = booking.status?.label?.toLowerCase() === "confirmed";

  return (
    <div>
      <div className="mb-4">
        <div className="d-flex flex-wrap align-items-center gap-2">
          <h5 className="fw-bold mb-0">{bookingTitle(booking)}</h5>
          <BookingStatusBadge value={booking.status?.label} />
        </div>
        <p className="text-sm text-secondary mt-1 mb-0">
          Référence {booking.reference}
        </p>
      </div>

      <Section title="Client">
        <Row icon={UserIcon} label="Nom" value={customer.fullName} />
        <Row icon={EnvelopeIcon} label="Email" value={customer.email} />
        <Row icon={PhoneIcon} label="Téléphone" value={customer.phone} />
      </Section>

      <Section title="Réservation">
        <Row
          icon={CalendarDaysIcon}
          label="Date"
          value={formatDateString(booking.date)}
        />
        <Row icon={ClockIcon} label="Heure" value={booking.hour} />
        <Row
          icon={UsersIcon}
          label="Participants"
          value={String(booking.personsNumber ?? 1)}
        />
        {variants?.map((item: any) => (
          <Row
            key={item.variant?.description}
            icon={TicketIcon}
            label="Formule"
            value={`${item.variant?.description} × ${item.numberOfReservation ?? 1}`}
          />
        ))}
      </Section>

      <Section title="Paiement">
        <Row
          icon={cashCollected ? BanknotesIcon : CreditCardIcon}
          label="Moyen de paiement"
          value={paymentMethodLabel(booking.paymentMethod)}
        />
        {booking.wafacashCode && (
          <Row
            icon={TicketIcon}
            label="Code Wafacash"
            value={booking.wafacashCode}
          />
        )}
        <Row label="Montant" value={formatBookingAmount(booking)} />
        <Row
          label="Commission partenaire"
          value={`+ ${formatMad(commission)}`}
          accent
        />
        {cashCollected && (
          <Row
            label="À régler à Venez"
            value={formatMad(bookingDueToVenez(booking))}
          />
        )}
        <Row
          label="Statut du paiement"
          value={
            confirmed
              ? "Payé"
              : cashCollected
                ? "Encaissé par le partenaire"
                : "En attente"
          }
        />
      </Section>

      {booking.createdAt && (
        <p className="text-xs text-secondary mb-0">
          Réservé le{" "}
          {new Intl.DateTimeFormat("fr-MA", {
            day: "2-digit",
            month: "long",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          }).format(new Date(booking.createdAt))}
        </p>
      )}
    </div>
  );
}
